import { useState, useCallback } from 'react';
import { apiService, parseApiError } from '../api-client';
import { User, LoginRequest, RegisterRequest, AuthApiResponse, RegisterApiResponse } from '../types/api';
import { useSession } from '@/stores/session';

interface AuthState {
  isLoading: boolean;
  error: string | null;
  errors: string[];
}

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  errors: string[];
  login: (credentials: LoginRequest) => Promise<boolean>;
  register: (userData: RegisterRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => void;
}

export const useAuth = (): UseAuthReturn => {
  // Zustand store'dan user state'ini oku
  const { user, setUser, clearUser } = useSession();
  
  // Local state sadece loading ve error için
  const [state, setState] = useState<AuthState>({
    isLoading: false,
    error: null,
    errors: [],
  });

  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null, errors: [] }));

    try {
      const response: AuthApiResponse = await apiService.auth.login(credentials);

      if (response.isSuccess && response.data) {
        // HttpOnly cookie'ler server-side'da set edildi
        // User bilgisi API response'dan geldi
        const user = response.data.user || null;

        if (user) {
          // Zustand store'a user'ı kaydet
          setUser(user);
        }

        setState({
          isLoading: false,
          error: null,
          errors: [],
        });

        return true;
      } else {
        const errorMessage = response.message || 'Login failed';
        const errors = response.errors || [];
        setState({
          isLoading: false,
          error: errorMessage,
          errors: errors,
        });
        return false;
      }
    } catch (error: unknown) {
      const { message, errors } = parseApiError(error, 'Login failed');
      
      setState({
        isLoading: false,
        error: message,
        errors: errors,
      });
      
      return false;
    }
  }, [setUser]);

  const register = useCallback(async (userData: RegisterRequest): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null, errors: [] }));

    try {
      const response: RegisterApiResponse = await apiService.auth.register(userData);

      if (response.isSuccess && response.data) {
        // HttpOnly cookie'ler server-side'da set edildi
        // User bilgisi API response'dan geldi
        const user = response.data.user || null;

        if (user) {
          // Zustand store'a user'ı kaydet
          setUser(user);
        }

        setState({
          isLoading: false,
          error: null,
          errors: [],
        });

        return true;
      } else {
        const errorMessage = response.message || 'Registration failed';
        const errors = response.errors || [];
        setState({
          isLoading: false,
          error: errorMessage,
          errors: errors,
        });
        return false;
      }
    } catch (error: unknown) {
      const { message, errors } = parseApiError(error, 'Registration failed');
      
      setState({
        isLoading: false,
        error: message,
        errors: errors,
      });
      
      return false;
    }
  }, [setUser]);

  const logout = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      await apiService.auth.logout();
    } catch (_error) {
      // Logout error - silent fail
    } finally {
      // Clear auth data regardless of API response
      clearUser();
      setState({
        isLoading: false,
        error: null,
        errors: [],
      });

      // Redirect to home page after logout
      if (typeof window !== 'undefined') {
        const currentLocale = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] || 'tr';
        window.location.href = `/${currentLocale}`;
      }
    }
  }, [clearUser]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null, errors: [] }));
  }, []);

  const refreshUser = useCallback(() => {
    try {
      // HttpOnly cookie kullanıldığında client-side'dan user bilgisi okunamaz
      // Bu fonksiyon artık çalışmayacak
      // Sayfa yenilenirse server component'ler güncel user bilgisini getirecek
      // refreshUser is not available with HttpOnly cookies. Refresh the page instead.
    } catch (_error) {
      // User refresh error - silent fail
    }
  }, []);

  return {
    user,
    isAuthenticated: !!user,
    ...state,
    login,
    register,
    logout,
    clearError,
    refreshUser,
  };
};
